/** Small payment-recovery overlay. Safe decline categories are translated,
 * never interpolated from a provider's raw message or risk decision. */
export const checkoutRecoveryCopy: Record<string, Record<string, string>> = {
  en: {
    insufficient_funds: "Your card has insufficient funds. Try another card or contact your bank.",
    expired_card: "Your card has expired. Use a different card.",
    incorrect_cvc: "The card security code is incorrect. Check it and try again.",
    authentication_required: "Your bank needs you to verify this payment. Try again and complete the bank's verification.",
    card_declined: "Your payment could not be completed. Try another card or contact your bank.",
    another_card: "Try another card", email_action: "Pay by link later",
  },
  da: {
    insufficient_funds: "Der er ikke tilstrækkelige midler på kortet. Prøv et andet kort, eller kontakt din bank.",
    expired_card: "Dit kort er udløbet. Brug et andet kort.",
    incorrect_cvc: "Kortets sikkerhedskode er forkert. Kontrollér den, og prøv igen.",
    authentication_required: "Din bank kræver, at du bekræfter betalingen. Prøv igen, og gennemfør bankens godkendelse.",
    card_declined: "Betalingen kunne ikke gennemføres. Prøv et andet kort, eller kontakt din bank.",
    another_card: "Prøv et andet kort", email_action: "Betal via link senere",
  },
  de: {
    insufficient_funds: "Deine Karte hat nicht genügend Guthaben. Verwende eine andere Karte oder kontaktiere deine Bank.",
    expired_card: "Deine Karte ist abgelaufen. Verwende eine andere Karte.",
    incorrect_cvc: "Der Sicherheitscode der Karte ist falsch. Prüfe ihn und versuche es erneut.",
    authentication_required: "Deine Bank muss diese Zahlung bestätigen. Versuche es erneut und schließe die Bestätigung ab.",
    card_declined: "Die Zahlung konnte nicht abgeschlossen werden. Verwende eine andere Karte oder kontaktiere deine Bank.",
    another_card: "Andere Karte verwenden", email_action: "Später per Link bezahlen",
  },
  es: {
    insufficient_funds: "Tu tarjeta no tiene fondos suficientes. Prueba otra tarjeta o contacta con tu banco.",
    expired_card: "Tu tarjeta ha caducado. Usa otra tarjeta.",
    incorrect_cvc: "El código de seguridad es incorrecto. Revísalo e inténtalo de nuevo.",
    authentication_required: "Tu banco necesita que verifiques el pago. Inténtalo de nuevo y completa la verificación.",
    card_declined: "No se pudo completar el pago. Prueba otra tarjeta o contacta con tu banco.",
    another_card: "Probar otra tarjeta", email_action: "Pagar por enlace más tarde",
  },
  fr: {
    insufficient_funds: "Les fonds de votre carte sont insuffisants. Essayez une autre carte ou contactez votre banque.",
    expired_card: "Votre carte a expiré. Utilisez une autre carte.",
    incorrect_cvc: "Le code de sécurité est incorrect. Vérifiez-le et réessayez.",
    authentication_required: "Votre banque vous demande de vérifier ce paiement. Réessayez et effectuez la vérification.",
    card_declined: "Le paiement n’a pas pu être effectué. Essayez une autre carte ou contactez votre banque.",
    another_card: "Essayer une autre carte", email_action: "Payer par lien plus tard",
  },
  it: {
    insufficient_funds: "La carta non ha fondi sufficienti. Prova un’altra carta o contatta la tua banca.",
    expired_card: "La carta è scaduta. Usa un’altra carta.",
    incorrect_cvc: "Il codice di sicurezza non è corretto. Controllalo e riprova.",
    authentication_required: "La banca richiede la verifica del pagamento. Riprova e completa la verifica.",
    card_declined: "Impossibile completare il pagamento. Prova un’altra carta o contatta la tua banca.",
    another_card: "Prova un’altra carta", email_action: "Paga tramite link più tardi",
  },
  nl: {
    insufficient_funds: "Je kaart heeft onvoldoende saldo. Probeer een andere kaart of neem contact op met je bank.",
    expired_card: "Je kaart is verlopen. Gebruik een andere kaart.",
    incorrect_cvc: "De beveiligingscode is onjuist. Controleer deze en probeer het opnieuw.",
    authentication_required: "Je bank vraagt om verificatie van deze betaling. Probeer opnieuw en voltooi de verificatie.",
    card_declined: "De betaling kon niet worden voltooid. Probeer een andere kaart of neem contact op met je bank.",
    another_card: "Andere kaart proberen", email_action: "Later via een link betalen",
  },
  no: {
    insufficient_funds: "Kortet har ikke tilstrekkelige midler. Prøv et annet kort eller kontakt banken.",
    expired_card: "Kortet ditt er utløpt. Bruk et annet kort.",
    incorrect_cvc: "Sikkerhetskoden er feil. Kontroller den og prøv igjen.",
    authentication_required: "Banken krever at du bekrefter betalingen. Prøv igjen og fullfør bekreftelsen.",
    card_declined: "Betalingen kunne ikke fullføres. Prøv et annet kort eller kontakt banken.",
    another_card: "Prøv et annet kort", email_action: "Betal via lenke senere",
  },
  sv: {
    insufficient_funds: "Kortet saknar tillräckligt med pengar. Prova ett annat kort eller kontakta banken.",
    expired_card: "Ditt kort har gått ut. Använd ett annat kort.",
    incorrect_cvc: "Säkerhetskoden är felaktig. Kontrollera den och försök igen.",
    authentication_required: "Banken kräver att du verifierar betalningen. Försök igen och slutför verifieringen.",
    card_declined: "Betalningen kunde inte slutföras. Prova ett annat kort eller kontakta banken.",
    another_card: "Prova ett annat kort", email_action: "Betala via länk senare",
  },
  cs: {
    insufficient_funds: "Na kartě není dostatek prostředků. Zkuste jinou kartu nebo kontaktujte banku.",
    expired_card: "Platnost karty vypršela. Použijte jinou kartu.",
    incorrect_cvc: "Bezpečnostní kód je nesprávný. Zkontrolujte jej a zkuste to znovu.",
    authentication_required: "Banka vyžaduje ověření platby. Zkuste to znovu a dokončete ověření.",
    card_declined: "Platbu se nepodařilo dokončit. Zkuste jinou kartu nebo kontaktujte banku.",
  },
  pl: {
    insufficient_funds: "Na karcie brakuje środków. Użyj innej karty lub skontaktuj się z bankiem.",
    expired_card: "Karta straciła ważność. Użyj innej karty.",
    incorrect_cvc: "Kod bezpieczeństwa jest nieprawidłowy. Sprawdź go i spróbuj ponownie.",
    authentication_required: "Bank wymaga weryfikacji płatności. Spróbuj ponownie i dokończ weryfikację.",
    card_declined: "Nie udało się zrealizować płatności. Użyj innej karty lub skontaktuj się z bankiem.",
  },
  sk: {
    insufficient_funds: "Na karte nie je dostatok prostriedkov. Skúste inú kartu alebo kontaktujte banku.",
    expired_card: "Platnosť karty vypršala. Použite inú kartu.",
    incorrect_cvc: "Bezpečnostný kód je nesprávny. Skontrolujte ho a skúste to znova.",
    authentication_required: "Banka vyžaduje overenie platby. Skúste to znova a dokončite overenie.",
    card_declined: "Platbu sa nepodarilo dokončiť. Skúste inú kartu alebo kontaktujte banku.",
  },
  bg: {
    insufficient_funds: "По картата няма достатъчно средства. Използвайте друга карта или се свържете с банката.",
    expired_card: "Срокът на картата е изтекъл. Използвайте друга карта.",
    incorrect_cvc: "Кодът за сигурност е неправилен. Проверете го и опитайте отново.",
    authentication_required: "Банката изисква потвърждение на плащането. Опитайте отново и завършете проверката.",
    card_declined: "Плащането не можа да бъде завършено. Използвайте друга карта или се свържете с банката.",
  },
  fi: {
    insufficient_funds: "Kortilla ei ole riittävästi katetta. Kokeile toista korttia tai ota yhteyttä pankkiisi.",
    expired_card: "Korttisi on vanhentunut. Käytä toista korttia.",
    incorrect_cvc: "Turvakoodi on virheellinen. Tarkista se ja yritä uudelleen.",
    authentication_required: "Pankkisi vaatii maksun vahvistamista. Yritä uudelleen ja suorita vahvistus loppuun.",
    card_declined: "Maksua ei voitu suorittaa. Kokeile toista korttia tai ota yhteyttä pankkiisi.",
  },
  "pt-BR": {
    insufficient_funds: "Seu cartão não tem saldo suficiente. Tente outro cartão ou fale com seu banco.",
    expired_card: "Seu cartão venceu. Use outro cartão.",
    incorrect_cvc: "O código de segurança está incorreto. Confira e tente novamente.",
    authentication_required: "Seu banco precisa verificar este pagamento. Tente novamente e conclua a verificação.",
    card_declined: "Não foi possível concluir o pagamento. Tente outro cartão ou fale com seu banco.",
  },
};
